import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth';
import rolePermissionService, { RolePermissionConfig } from '../services/rolePermissions';
import { User, UserRole } from '../types/auth';
import ConfirmDialog from '../components/ConfirmDialog';
import authExtended from '../services/auth_extended';

const AdminUsers = () => {
    const { isAdmin, user: currentUser } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newUser, setNewUser] = useState({ email: '', name: '', role: 'guest' as UserRole });

    // Role permissions configuration
    const [roleConfig, setRoleConfig] = useState<Record<string, RolePermissionConfig>>({});
    const [savingPermissions, setSavingPermissions] = useState<string | null>(null);
    const [editingRole, setEditingRole] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<Record<string, boolean>>({});

    // Confirmation dialogs
    const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; email: string; name: string }>({
        show: false,
        email: '',
        name: ''
    });

    // User editing state
    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ name: string; role: UserRole }>({ name: '', role: 'guest' });

    useEffect(() => {
        loadUsers();
        loadRolePermissions();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const fetchedUsers = await authService.getAllUsers();
            setUsers(fetchedUsers);
            setError(null);
        } catch (err) {
            setError('載入用戶列表失敗');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadRolePermissions = async () => {
        try {
            const permissions = await rolePermissionService.getAllRolePermissions();
            setRoleConfig(permissions);
        } catch (err) {
            console.error('Failed to load role permissions:', err);
        }
    };

    const handleStartEdit = (user: User) => {
        setEditingUser(user.email);
        setEditForm({ name: user.name, role: user.role });
    };

    const handleCancelEdit = () => {
        setEditingUser(null);
        setEditForm({ name: '', role: 'guest' });
    };

    const handleSaveEdit = async (email: string) => {
        try {
            // Update user role if changed
            const user = users.find(u => u.email === email);
            if (user && editForm.role !== user.role) {
                await authService.updateUserRole(email, editForm.role);
            }

            // Update local state
            setUsers(users.map(u =>
                u.email === email
                    ? { ...u, name: editForm.name, role: editForm.role }
                    : u
            ));

            setEditingUser(null);
            setEditForm({ name: '', role: 'guest' });
        } catch (err) {
            alert('更新用戶失敗：' + (err as Error).message);
            console.error(err);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const created = await authService.createUser(newUser.email, newUser.name, newUser.role);
            setUsers([created, ...users]);
            setNewUser({ email: '', name: '', role: 'guest' });
            setShowCreateForm(false);
        } catch (err) {
            alert('創建用戶失敗：' + (err as Error).message);
            console.error(err);
        }
    };

    const handleDeleteUser = async () => {
        try {
            await authExtended.deleteUser(deleteConfirm.email);
            setUsers(users.filter(u => u.email !== deleteConfirm.email));
            setDeleteConfirm({ show: false, email: '', name: '' });
        } catch (err) {
            alert('刪除用戶失敗：' + (err as Error).message);
            console.error(err);
        }
    };

    const handleSaveRolePermissions = async (role: UserRole) => {
        if (role === 'admin') return;

        try {
            setSavingPermissions(role);
            const config = roleConfig[role];
            await rolePermissionService.updateRolePermissions(role, config.pages, config.canOperate);
            setHasUnsavedChanges(prev => ({ ...prev, [role]: false }));
            setEditingRole(null);
        } catch (err) {
            alert('更新權限失敗：' + (err as Error).message);
            console.error(err);
        } finally {
            setSavingPermissions(null);
        }
    };

    const handlePermissionChange = (role: UserRole, pagePath: string, checked: boolean) => {
        const newConfig = { ...roleConfig };
        const currentPages = newConfig[role]?.pages || [];

        if (checked) {
            newConfig[role] = {
                ...newConfig[role],
                role,
                pages: [...currentPages, pagePath],
                canOperate: newConfig[role]?.canOperate ?? true
            };
        } else {
            newConfig[role] = {
                ...newConfig[role],
                role,
                pages: currentPages.filter(p => p !== pagePath),
                canOperate: newConfig[role]?.canOperate ?? true
            };
        }

        setRoleConfig(newConfig);
        setHasUnsavedChanges(prev => ({ ...prev, [role]: true }));
    };

    const getRoleBadgeColor = (role: UserRole) => {
        switch (role) {
            case 'admin':
                return 'bg-red-100 text-red-800';
            case 'qa':
                return 'bg-purple-100 text-purple-800';
            case 'user':
                return 'bg-blue-100 text-blue-800';
            case 'guest':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getRoleDisplayName = (role: UserRole) => {
        switch (role) {
            case 'admin':
                return 'Admin';
            case 'qa':
                return 'QA';
            case 'user':
                return 'AmazingTalker';
            case 'guest':
                return 'Guest';
            default:
                return role;
        }
    };

    if (!isAdmin()) {
        return (
            <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">您沒有權限訪問此頁面</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">用戶管理</h1>
                <p className="mt-2 text-sm text-gray-600">管理系統用戶和角色權限配置</p>
            </div>

            {error && (
                <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                </div>
            )}

            {/* Create User Form */}
            <div className="mb-8">
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    {showCreateForm ? '取消新增' : '+ 新增用戶'}
                </button>

                {showCreateForm && (
                    <div className="mt-4 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">新增用戶</h3>
                        <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="user@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">名稱</label>
                                <input
                                    type="text"
                                    required
                                    value={newUser.name}
                                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="使用者名稱"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="guest">Guest</option>
                                    <option value="user">AmazingTalker</option>
                                    <option value="qa">QA</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div className="md:col-span-3">
                                <button
                                    type="submit"
                                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    創建用戶
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-12">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                用戶
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                角色
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                最後登入
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                                    載入中...
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                                    尚無用戶
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => {
                                const isEditing = editingUser === user.email;

                                return (
                                    <tr key={user.email} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                {user.avatar && (
                                                    <img
                                                        src={user.avatar}
                                                        alt={user.name}
                                                        className="h-10 w-10 rounded-full mr-3"
                                                    />
                                                )}
                                                <div className="flex-1">
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editForm.name}
                                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                            className="w-full px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                    )}
                                                    <div className="text-sm text-gray-500">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {isEditing ? (
                                                <select
                                                    value={editForm.role}
                                                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as UserRole })}
                                                    className="px-3 py-1 text-xs font-semibold rounded-full border border-blue-300 focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="guest">Guest</option>
                                                    <option value="user">AmazingTalker</option>
                                                    <option value="qa">QA</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                            ) : (
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                                                    {getRoleDisplayName(user.role)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {user.lastLoginAt && user.createdAt && user.lastLoginAt !== user.createdAt
                                                ? new Date(user.lastLoginAt).toLocaleString('zh-TW')
                                                : <span className="text-gray-400">未登入</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                                                        >
                                                            取消
                                                        </button>
                                                        <button
                                                            onClick={() => handleSaveEdit(user.email)}
                                                            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                                                        >
                                                            儲存
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => handleStartEdit(user)}
                                                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                                                        >
                                                            編輯
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm({ show: true, email: user.email, name: user.name })}
                                                            disabled={currentUser?.email === user.email}
                                                            className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            刪除
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Role Management Section */}
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">角色權限管理</h2>
                    <p className="mt-1 text-sm text-gray-600">配置每個角色的頁面訪問權限</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(Object.keys(roleConfig) as UserRole[]).map((role) => {
                        const isEditing = editingRole === role;
                        const hasChanges = hasUnsavedChanges[role];

                        return (
                            <div key={role} className={`bg-white p-6 rounded-lg border-2 transition-all ${isEditing ? 'border-blue-500 shadow-lg' : 'border-gray-200 shadow-sm'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getRoleBadgeColor(role)}`}>
                                            {getRoleDisplayName(role)}
                                        </span>
                                        {hasChanges && (
                                            <span className="text-xs text-orange-600 font-medium">● 未儲存</span>
                                        )}
                                    </div>
                                    {role !== 'admin' && (
                                        <div className="flex gap-2">
                                            {!isEditing ? (
                                                <button
                                                    onClick={() => setEditingRole(role)}
                                                    className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                                                >
                                                    編輯
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => {
                                                            setEditingRole(null);
                                                            if (hasChanges) {
                                                                loadRolePermissions(); // Reload to discard changes
                                                                setHasUnsavedChanges(prev => ({ ...prev, [role]: false }));
                                                            }
                                                        }}
                                                        className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                                                    >
                                                        取消
                                                    </button>
                                                    <button
                                                        onClick={() => handleSaveRolePermissions(role)}
                                                        disabled={savingPermissions === role || !hasChanges}
                                                        className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        {savingPermissions === role ? '儲存中...' : '儲存'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-gray-700 mb-3">頁面訪問權限：</p>
                                    <div className="space-y-2">
                                        {[
                                            { path: '/', label: '首頁' },
                                            { path: '/test-plan', label: '測試計劃生成' },
                                            { path: '/virtual-room', label: '虛擬教室創建' },
                                            { path: '/admin/users', label: '用戶管理' }
                                        ].map((page) => {
                                            const isChecked = role === 'admin' || roleConfig[role]?.pages.includes(page.path);
                                            const isAdminRole = role === 'admin';
                                            const isDisabled = !isEditing || isAdminRole || (page.path === '/admin/users' && !isAdminRole);

                                            return (
                                                <label
                                                    key={page.path}
                                                    className={`flex items-center space-x-3 p-2 rounded transition-all ${isEditing && !isDisabled ? 'hover:bg-blue-50 cursor-pointer' : ''
                                                        } ${isDisabled ? 'opacity-50' : ''
                                                        }`}
                                                >
                                                    <div className="relative flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            disabled={isDisabled}
                                                            onChange={(e) => handlePermissionChange(role, page.path, e.target.checked)}
                                                            className="peer sr-only"
                                                        />
                                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${isDisabled
                                                            ? isChecked
                                                                ? 'bg-gray-200 border-gray-300'
                                                                : 'bg-gray-100 border-gray-300'
                                                            : isChecked
                                                                ? 'bg-blue-600 border-blue-600'
                                                                : 'bg-white border-gray-400 peer-hover:border-blue-500'
                                                            }`}>
                                                            {isChecked && (
                                                                <svg className={`w-3 h-3 ${isDisabled ? 'text-gray-400' : 'text-white'
                                                                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <span className={`text-sm ${isDisabled ? 'text-gray-500' : 'text-gray-700 font-medium'
                                                        }`}>{page.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {role === 'admin' && (
                                        <p className="text-xs text-gray-500 mt-3 italic">* 管理員永久擁有所有權限</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.show}
                title="確認刪除用戶"
                message={`確定要刪除用戶「${deleteConfirm.name}」(${deleteConfirm.email}) 嗎？此操作無法復原。`}
                confirmText="刪除"
                cancelText="取消"
                onConfirm={handleDeleteUser}
                onCancel={() => setDeleteConfirm({ show: false, email: '', name: '' })}
            />
        </div>
    );
};

export default AdminUsers;
